/**
 * The decide transaction: a scratch fold. Handlers emit events one by one; each event is evolved into the scratch
 * state immediately, so a later change in the same command sees what an earlier one created (a changeset can
 * reference ids it creates). A rejection is thrown as `Rejected` and caught by `decide`: it writes no event.
 */
import { evolve } from "../evolve"
import { canonicalJson } from "../hash"
import { ownerOfActor } from "../policy"
import { nextItemRef } from "../refs"
import { principalOf, runOf } from "../state"
import type {
  Actor,
  ChangesetId,
  CommandRejection,
  Comment,
  EntityRef,
  EventMeta,
  Ids,
  PolicyStamp,
  Principal,
  ProjectEvent,
  ProjectState,
  ProposalId,
  Taint,
  UserId,
} from "../types"

/** The payload version every event decide emits is stamped with (decision 0036: 1 = before the cutover). */
export const EVENT_VERSION = 2

/** Everything decide may know besides state. No clock reads and no randomness inside core: both arrive here. */
export interface DecideContext {
  readonly actor: Actor // stamped from the credential
  readonly now: number // the DO's clock at append; becomes meta.ts
  readonly correlationId: string
  /** The lease epoch the caller presented (runner reports, MCP calls). Checked by the fence for run-scoped commands. */
  readonly epoch?: number
  /** Read access to history for undo and revert (the DO supplies it from its log; absent in dry runs → Invalid). */
  readonly history?: DecideHistory
}

export interface DecideHistory {
  /** Events of one applied changeset (contiguous in the log, closed by its ChangesetApplied). */
  readonly changesetEvents: (changesetId: ChangesetId) => ReadonlyArray<ProjectEvent> | undefined
  /** The plan state at version v (the log folded up to ChangesetApplied(v)). */
  readonly stateAtVersion: (version: number) => ProjectState | undefined
  /** Changesets applied after the given one, for undo conflict detection. */
  readonly changesetsAfter?: (changesetId: ChangesetId) => ReadonlyArray<ReadonlyArray<ProjectEvent>>
  /** A comment by id (comments other than open asks live in the comments projection, not in state). */
  readonly comment?: (commentId: Ids["CommentId"]) => Comment | undefined
  /** The applied changeset that undid this one (its `undoOf`), if any: Undo refuses to undo a change twice. */
  readonly undoneBy?: (changesetId: ChangesetId) => ChangesetId | undefined
}

/** Per-event metadata decide decides (the rest is stamped by the DO at append). */
export interface Annotation {
  readonly actor: Actor
  readonly changesetId: ChangesetId | null
  readonly policy: PolicyStamp | null
  readonly approvedBy: { readonly proposalId: ProposalId; readonly by: Actor } | null
}

export class Rejected {
  readonly _tag = "Rejected"
  constructor(readonly rejection: CommandRejection) {}
}

// Function declarations (not const arrows) so TypeScript narrows after a call: they are assertion points.
export function reject(rejection: CommandRejection): never {
  throw new Rejected(rejection)
}
export function invalid(message: string, path = "", changeIndex: number | null = null): never {
  throw new Rejected({ _tag: "Invalid", issues: [{ path, message, changeIndex }] })
}
export function forbidden(
  reason: Extract<CommandRejection, { _tag: "Forbidden" }>["reason"],
  detail: string,
): never {
  throw new Rejected({ _tag: "Forbidden", reason, detail })
}
export function notFound(ref: EntityRef): never {
  throw new Rejected({ _tag: "NotFound", ref })
}

/** Run taint of an actor (agent/worker inside a run), clean otherwise. */
export const actorTaint = (s: ProjectState, actor: Actor): Taint => {
  if (actor._tag !== "agent" && actor._tag !== "worker") return "clean"
  return runOf(s, actor.runId)?.taint ?? "clean"
}

export class Tx {
  state: ProjectState
  readonly events: ProjectEvent[] = []
  readonly annotations: Annotation[] = []
  /** Defaults for annotations of events emitted while set (changeset folding, approved proposals). */
  scope: Partial<Annotation> = {}

  constructor(
    readonly base: ProjectState,
    readonly ctx: DecideContext,
  ) {
    this.state = base
  }

  get actor(): Actor {
    return this.scope.actor ?? this.ctx.actor
  }
  get now(): number {
    return this.ctx.now
  }

  /** Decision 0116: who writes what is emitted now: the person who approved the card that carries it, else the actor. */
  writer(): Principal | null {
    const by = this.scope.approvedBy?.by
    return by?._tag === "user" ? { _tag: "user", userId: by.userId } : principalOf(this.actor)
  }

  /** The person records created now belong to. */
  owner(): UserId {
    const o = ownerOfActor(this.state, this.actor)
    if (o === null) invalid("project has no owner yet")
    return o
  }

  meta(ann: Annotation): EventMeta {
    return {
      seq: (this.state.seq + 1) as Ids["Seq"],
      ts: this.ctx.now,
      v: EVENT_VERSION,
      actor: ann.actor,
      commandId: null,
      causedBy: null,
      links: [],
      correlationId: this.ctx.correlationId,
      changesetId: ann.changesetId,
      policy: ann.policy,
      approvedBy: ann.approvedBy,
      taint: actorTaint(this.state, ann.actor),
    }
  }

  /**
   * Decision 0144: the `ask` and `recommendation` an `OpenProposal` carries, stamped on the card it opens (whichever
   * path files it: a question, a plan card, a monitor). Set by `openProposalCmd` for its own proposal id.
   */
  cardShape: {
    readonly proposalId: string
    readonly ask?: string | undefined
    readonly recommendation?: { readonly option: string; readonly reason: string } | undefined
  } | null = null

  emit(input: ProjectEvent, ann: Partial<Annotation> = {}): void {
    const full: Annotation = {
      actor: ann.actor ?? this.scope.actor ?? this.ctx.actor,
      changesetId: ann.changesetId !== undefined ? ann.changesetId : (this.scope.changesetId ?? null),
      policy: ann.policy !== undefined ? ann.policy : (this.scope.policy ?? null),
      approvedBy: ann.approvedBy !== undefined ? ann.approvedBy : (this.scope.approvedBy ?? null),
    }
    const event = this.stamp(input, full)
    this.state = evolve(this.state, event, this.meta(full))
    this.events.push(event)
    this.annotations.push(full)
  }

  /**
   * Decision 0144: what decide adds at emit time, whatever path emits the event: a card's `ask` and `recommendation`
   * (see `cardShape`), and the D-n of a decision: a card approved, rejected or answered by a person or an agent
   * (Minga's own processes record none), and a `decision` comment. An event that already carries one keeps it.
   */
  private stamp(event: ProjectEvent, ann: Annotation): ProjectEvent {
    const shape = this.cardShape
    if (event._tag === "ProposalOpened" && shape && shape.proposalId === event.proposal.id) {
      const p = event.proposal
      return {
        ...event,
        proposal: {
          ...p,
          ...(shape.ask !== undefined ? { ask: shape.ask } : {}),
          ...(shape.recommendation !== undefined ? { recommendation: shape.recommendation } : {}),
        },
      }
    }
    const byDecider = ann.actor._tag === "user" || ann.actor._tag === "agent"
    if (
      byDecider &&
      (event._tag === "ProposalApproved" ||
        event._tag === "ProposalRejected" ||
        event._tag === "ProposalAnswered") &&
      event.decisionRef === undefined
    )
      return { ...event, decisionRef: this.nextDecisionRef() }
    if (
      event._tag === "CommentPosted" &&
      event.comment.kind === "decision" &&
      event.comment.ref === undefined
    )
      return { ...event, comment: { ...event.comment, ref: this.nextDecisionRef() } }
    return event
  }

  /** The next decision ref, D-n (decision 0144; never reused). */
  nextDecisionRef(): Ids["ShortRef"] {
    return `D-${(this.state.refCounters.D ?? 0) + 1}` as Ids["ShortRef"]
  }

  /** Run `f` with annotation defaults, restoring the previous scope afterwards. */
  withScope<A>(scope: Partial<Annotation>, f: () => A): A {
    const prev = this.scope
    this.scope = { ...prev, ...scope }
    try {
      return f()
    } finally {
      this.scope = prev
    }
  }

  /** Next short ref for a kind, e.g. P-13 (never reused). Items use `nextItemRef`. */
  nextRef(kind: "G" | "K" | "P" | "R"): Ids["ShortRef"] {
    return `${kind}-${this.state.refCounters[kind] + 1}` as Ids["ShortRef"]
  }

  /** Next item ref, e.g. DIC-13 (decision 0035): never repeats an old T or I number. */
  nextItemRef(): Ids["ShortRef"] {
    return nextItemRef(this.state)
  }

  /** Next temporary-teammate ref (settings wave K1): W-n for workers, V-n for validators (never reused). */
  nextWorkerRef(kind: "W" | "V"): Ids["WorkerRef"] {
    return `${kind}-${(this.state.refCounters[kind] ?? 0) + 1}` as Ids["WorkerRef"]
  }

  /** RecordMeta for a record created now. */
  recordMeta() {
    const a = this.actor
    return {
      ownerId: this.owner(),
      visibility: "workspace" as const,
      createdBy: a,
      createdAt: this.now,
      updatedBy: a,
      updatedAt: this.now,
      lastSeq: (this.state.seq + 1) as Ids["Seq"],
    }
  }
}

/** Is the acting principal a person? */
export const isHuman = (a: Actor): a is Extract<Actor, { _tag: "user" }> => a._tag === "user"

/** Base62 fractional rank strictly after `last` (or a middle value when there is none). ASCII order = base62 order. */
export const rankAfter = (last: string | null): string => {
  if (last === null || last.length === 0) return "U"
  const ALPHA = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
  const ch = last[last.length - 1] ?? "z"
  const i = ALPHA.indexOf(ch)
  if (i >= 0 && i < ALPHA.length - 1) return last.slice(0, -1) + ALPHA[Math.min(ALPHA.length - 1, i + 1)]
  return `${last}U`
}

export const maxRank = (ranks: ReadonlyArray<string>): string | null =>
  ranks.length === 0 ? null : ranks.reduce((a, b) => (b > a ? b : a))

/** The subset of `current` named by the keys present in `patch` (the `before` of a patch event). */
export const beforeOf = <P extends object>(current: object, patch: P): P => {
  const out: Record<string, unknown> = {}
  const cur = current as Record<string, unknown>
  for (const k of Object.keys(patch)) if ((patch as Record<string, unknown>)[k] !== undefined) out[k] = cur[k]
  return out as P
}

/** Structural equality for actors (used to tell "this command's own proposal" apart). */
export const sameActor = (a: Actor, b: Actor): boolean => canonicalJson(a) === canonicalJson(b)
